import { addWeeks } from "date-fns"
import type { ForecastBasis } from "~/constants/npi"
import { type RouterOutputs } from "~/utils/api"
import { sumNumberArrays } from "~/utils/tsUtils"

/**
 * Aggregate per-variant historic forecast arrays into a single Map keyed by week timestamp.
 *
 * `forecast[i]` on each variant is placed at `addWeeks(firstDay, i + weekOffset)` and summed
 * across variants. Used for both weekDiff mode (offset = weekDiff) and snapshot-date mode (offset = 0).
 *
 * Optional `normalizeFirstDay` lets callers apply timezone normalization (e.g. frontend needs
 * to map serialized dates back to Monday 00:00 UTC to align with grid timestamps).
 * Optional `filter` lets callers drop certain weeks (e.g. past-only for the grid overlay).
 */
export function aggregateHistoricForecastByWeek(
  variants: ReadonlyArray<{ firstDay: Date; forecast: number[] }>,
  {
    weekOffset = 0,
    normalizeFirstDay,
    filter,
  }: {
    weekOffset?: number
    normalizeFirstDay?: (d: Date) => Date
    filter?: (weekMs: number) => boolean
  } = {},
): Map<number, number> {
  const byWeek = new Map<number, number>()
  for (const v of variants) {
    const firstDay = normalizeFirstDay ? normalizeFirstDay(v.firstDay) : v.firstDay
    for (let i = 0; i < v.forecast.length; i++) {
      const dateMs = addWeeks(firstDay, i + weekOffset).getTime()
      if (filter && !filter(dateMs)) continue
      byWeek.set(dateMs, (byWeek.get(dateMs) ?? 0) + v.forecast[i]!)
    }
  }
  return byWeek
}

type INpiVariantWithSales = RouterOutputs["npi"]["getNearestNeighborsWithSales"][0]
type INpiCustomMatchWithSales = RouterOutputs["npi"]["getCustomMatchesWithSales"][0]

/** Pad shorter cumulative arrays with their last value, then sum element-wise. */
export function padAndSumCumulativeArrays(arrays: number[][]): number[] {
  if (arrays.length === 0) return []
  if (arrays.length === 1) return arrays[0] ?? []

  const maxLen = Math.max(...arrays.map((a) => a.length))
  const padded = arrays.map((a) => {
    if (a.length >= maxLen) return a
    const lastVal = a[a.length - 1] ?? 0
    return [...a, ...Array<number>(maxLen - a.length).fill(lastVal)]
  })
  return sumNumberArrays(padded)
}

export function getForecastsForWeeks(forecasts: number[], weeks: number) {
  if (weeks <= 0) return 0
  if (forecasts.length < weeks) {
    const lastForecast = forecasts[forecasts.length - 1] ?? 0
    return (
      lastForecast +
      (lastForecast - (forecasts[forecasts.length - 2] ?? 0)) * (weeks - forecasts.length)
    )
  }
  const ratio = weeks % 1
  const idx = Math.floor(weeks) - 1
  return (1 - ratio) * (forecasts[idx] ?? 0) + ratio * (forecasts[idx + 1] ?? 0)
}

export function getForecastBetweenWeeks(forecasts: number[], startWeek: number, endWeek: number) {
  if (startWeek > endWeek) return 0
  if (endWeek <= 0) return 0
  startWeek = Math.max(0, startWeek)
  return getForecastsForWeeks(forecasts, endWeek) - getForecastsForWeeks(forecasts, startWeek)
}

/**
 * Period total as the sum of per-week rounded values (weeks summed across `seriesList` before
 * rounding, matching the weekly cell) so whole-week periods cross-check against displayed weeks.
 * Requires integer week bounds.
 */
export function getForecastBetweenWeeksRounded(
  seriesList: number[][],
  startWeek: number,
  endWeek: number,
) {
  let total = 0
  for (let week = Math.max(0, startWeek); week < endWeek; week++) {
    let weekTotal = 0
    for (const series of seriesList) weekTotal += getForecastBetweenWeeks(series, week, week + 1)
    total += Math.round(weekTotal)
  }
  return total
}

export function getForecastedReturnsBetweenWeeks(
  returns: { weekDiff: number; quantity: number }[] | null | undefined,
  startWeek: number,
  endWeek: number,
): number {
  if (!returns) return 0
  let total = 0
  for (const r of returns) {
    if (r.weekDiff > startWeek && r.weekDiff <= endWeek) total += r.quantity
  }
  return total
}

export function buildWeeklyForecastSeries(
  cumulativeForecasts: number[],
  baseDateMs: number,
  weekStartDates: Date[],
): { date: Date; value: number }[] {
  const msPerWeek = 7 * 24 * 60 * 60 * 1000
  return weekStartDates
    .filter((weekStart) => (weekStart.getTime() - baseDateMs) / msPerWeek + 1 > 0)
    .map((weekStart) => {
      const startWeek = (weekStart.getTime() - baseDateMs) / msPerWeek
      const endWeek = startWeek + 1
      return {
        date: weekStart,
        value: getForecastBetweenWeeks(cumulativeForecasts, startWeek, endWeek),
      }
    })
}

export const NEVER_STOCKOUT_WEEK = 1000

export function getWeekOfHittingThreshold(threshold: number, forecasts: number[]) {
  const sumForecasts = forecasts[forecasts.length - 1] ?? 0
  if (threshold >= sumForecasts) {
    const secondLastForecast = forecasts[forecasts.length - 2] ?? 0
    // Just to not give exactly the same week
    if (sumForecasts === secondLastForecast)
      return NEVER_STOCKOUT_WEEK + ((threshold - sumForecasts) / sumForecasts) * forecasts.length
    return forecasts.length + (threshold - sumForecasts) / (sumForecasts - secondLastForecast)
  }
  const idx = forecasts.findIndex((f) => f > threshold)
  const weeksForecast = (forecasts[idx] ?? 0) - (forecasts[idx - 1] ?? 0)
  if (weeksForecast === 0) return idx
  return idx + (threshold - (forecasts[idx - 1] ?? 0)) / weeksForecast
}

export const getSalesForMatch = (
  match: INpiVariantWithSales | INpiCustomMatchWithSales,
  forecastBasis: ForecastBasis,
  specificYear?: number,
) => {
  switch (forecastBasis) {
    case "firstYear":
      return match.firstYearSales ?? 0
    case "lastYear":
      return match.lastYearSales ?? 0
    case "yearlyAverage":
      return match.yearlyAverageSales ?? 0
    case "specificYear":
      if (!specificYear || !match.allYearlySales) return undefined
      const yearData = match.allYearlySales.find((y) => y.year === specificYear)
      return yearData?.sales ?? 0
    default:
      return match.firstYearSales ?? 0
  }
}
